import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { PlotDataProvider } from "../../context/PlotDataContext";
import { ProvenanceProvider } from "../../context/ProvenanceContext";
import { ViewStateProvider } from "../../context/ViewStateContext";
import { fetchProject, type Project } from "../../api/projects";
import {
  createThreadV4NotebookEntry,
  createThreadV4View,
  fetchThreadV4Notebook,
  fetchThreadV4Views,
  updateThreadV4View,
  type ThreadV4NotebookEntry,
  type ThreadV4NotebookEntryType,
  type ThreadV4SpoolSourceType,
  type ThreadV4SpoolVisibility,
  type ThreadV4ViewSpec,
  type ThreadV4ViewVisibility,
  type ThreadV4WidgetDock,
  type ThreadV4WidgetKind,
} from "../../api/threadV4Views";
import { useV3Data } from "../thread-v3/useV3Data";
import { SaveDialog, SelectionRail, TopChrome, ViewTabs, type V4ActiveViewId } from "./ThreadV4Chrome";
import { CustomLayoutView, CustomizeFlow, DefaultThreadView } from "./ThreadV4Stages";
import { rowKey } from "./ThreadV4Widgets";
import { autoArrangeThreadV4Widgets, dockThreadV4Widget, isUuidLike, makeCustomDashboardTemplate, makeThreadV4Widget } from "./viewSpecs";
import "./thread-v4.css";

function requestedProjectId(routeProjectId: string | undefined, searchParams: URLSearchParams): string | undefined {
  return routeProjectId ?? searchParams.get("project") ?? undefined;
}

function activeViewFromSearch(search: string): V4ActiveViewId {
  const requested = new URLSearchParams(search).get("view")?.trim();
  if (requested === "cfd" || requested === "data" || requested === "analysis" || requested === "power") return "home";
  return requested || "home";
}

function noteAuthorLabel(entryType: ThreadV4NotebookEntryType): string {
  if (entryType === "agent") return "Agent";
  if (entryType === "system") return "System";
  if (entryType === "decision") return "Decision";
  if (entryType === "task") return "Task";
  return "You";
}

function localV4StorageKey(projectId: string, branch: string, kind: "views" | "notebook"): string {
  return `thread:v4:local:${projectId}:${branch}:${kind}`;
}

function readLocalV4Views(projectId: string, branch: string): ThreadV4ViewSpec[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(localV4StorageKey(projectId, branch, "views")) ?? "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalV4Views(projectId: string, branch: string, views: ThreadV4ViewSpec[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(localV4StorageKey(projectId, branch, "views"), JSON.stringify(views));
}

function readLocalV4Notebook(projectId: string, branch: string): ThreadV4NotebookEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(localV4StorageKey(projectId, branch, "notebook")) ?? "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalV4Notebook(projectId: string, branch: string, entries: ThreadV4NotebookEntry[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(localV4StorageKey(projectId, branch, "notebook"), JSON.stringify(entries));
}

function localNotebookEntry(
  projectId: string,
  branch: string,
  entryType: ThreadV4NotebookEntryType,
  sourceType: ThreadV4SpoolSourceType,
  sourceUri: string,
  visibility: ThreadV4SpoolVisibility,
  body: string,
): ThreadV4NotebookEntry {
  return {
    id: `local-note-${Date.now()}`,
    project_id: projectId,
    view_id: null,
    branch,
    entry_type: entryType,
    author_id: "local-user",
    author_label: noteAuthorLabel(entryType),
    owner_user_id: "local-user",
    visibility,
    source_type: sourceType,
    source_uri: sourceUri || null,
    source_external_id: null,
    source_created_at: null,
    captured_at: new Date().toISOString(),
    body,
    body_format: "markdown",
    anchors: {},
    source_metadata: {},
    metadata: { source: "thread-v4-workbench", optimistic: true },
    created_at: new Date().toISOString(),
    updated_at: null,
  };
}

const WIDGET_LABELS: Record<ThreadV4WidgetKind, string> = {
  "asset-rail": "Assets",
  "build-surface": "Build surface",
  dag: "DAG",
  flowfield: "Flowfield",
  geometry: "Geometry",
  "markdown-note": "Note",
  mesh: "Mesh",
  notebook: "Spool notebook",
  plot: "Plot",
  table: "Table",
};

const WIDGET_SIZES: Record<ThreadV4WidgetKind, { w: number; h: number; dock?: ThreadV4WidgetDock }> = {
  "asset-rail": { w: 4, h: 2, dock: "right" },
  "build-surface": { w: 3, h: 3, dock: "left" },
  dag: { w: 4, h: 2 },
  flowfield: { w: 5, h: 3, dock: "right" },
  geometry: { w: 3, h: 2, dock: "left" },
  "markdown-note": { w: 4, h: 2 },
  mesh: { w: 3, h: 3, dock: "left" },
  notebook: { w: 5, h: 3, dock: "bottom" },
  plot: { w: 5, h: 2, dock: "bottom" },
  table: { w: 6, h: 3 },
};

function makeDraftView(projectId: string, branch: string): ThreadV4ViewSpec {
  const draft = makeCustomDashboardTemplate(projectId, branch, Date.now().toString(36));
  return {
    ...draft,
    id: `draft-${draft.id}`,
    name: "Untitled view",
    slug: `draft-${draft.slug}`,
    metadata: { ...draft.metadata, draft: true },
  };
}

function appendWidgetToView(view: ThreadV4ViewSpec, kind: ThreadV4WidgetKind): ThreadV4ViewSpec {
  const spec = WIDGET_SIZES[kind];
  const maxY = view.layout_json.widgets.reduce((next, widget) => Math.max(next, widget.position.y + widget.position.h), 0);
  const id = `${kind}-${Date.now().toString(36)}`;
  const widget = makeThreadV4Widget(id, kind, WIDGET_LABELS[kind], 0, maxY, spec.w, spec.h, spec.dock ?? "canvas", kind === "notebook" ? { scope: "project", sources: ["manual", "agent", "slack", "gmail", "drive", "flow360", "github"] } : {});
  return { ...view, layout_json: autoArrangeThreadV4Widgets({ ...view.layout_json, widgets: [...view.layout_json.widgets, widget] }) };
}

export default function ThreadV4WorkbenchPage() {
  const { projectId: routeProjectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeProject = requestedProjectId(routeProjectId, searchParams);
  const requestedBranch = searchParams.get("branch");
  const dataSearchParams = new URLSearchParams(searchParams);
  dataSearchParams.delete("view");
  const v3State = useV3Data(routeProject, requestedBranch, dataSearchParams.toString());
  const data = v3State.data;
  const projectId = routeProject ?? data.project?.id ?? "local-thread-project";
  const branch = data.branch || requestedBranch || "main";
  const persistedProject = isUuidLike(projectId);
  const [projectSummary, setProjectSummary] = useState<Project | null>(null);
  const fixtureName = searchParams.get("fixture");
  const displayProjectName = projectSummary?.name ?? data.project?.name ?? (fixtureName ? `${fixtureName} fixture` : "Local Thread data");
  const scopeLabel = persistedProject ? "Project" : fixtureName ? "Local fixture" : "Local workspace";
  const sourceStatus = v3State.status === "ready" ? (persistedProject ? "ready" : "fixture ready") : v3State.status;

  const [serverViews, setServerViews] = useState<ThreadV4ViewSpec[]>([]);
  const [localViews, setLocalViews] = useState<ThreadV4ViewSpec[]>([]);
  const [draftView, setDraftView] = useState<ThreadV4ViewSpec | null>(null);
  const [activeView, setActiveView] = useState<V4ActiveViewId>(() => activeViewFromSearch(location.search));
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"save" | null>(null);
  const [statusMessage, setStatusMessage] = useState("Ready");
  const [notebookEntries, setNotebookEntries] = useState<ThreadV4NotebookEntry[]>([]);
  const [notebookDraft, setNotebookDraft] = useState("");
  const [notebookType, setNotebookType] = useState<ThreadV4NotebookEntryType>("note");
  const [notebookSourceType, setNotebookSourceType] = useState<ThreadV4SpoolSourceType>("manual");
  const [notebookSourceUri, setNotebookSourceUri] = useState("");
  const [notebookVisibility, setNotebookVisibility] = useState<ThreadV4SpoolVisibility>("work_shared");
  const persistenceStatus = persistedProject ? statusMessage : "local-only views";

  useEffect(() => {
    let cancelled = false;
    if (!persistedProject) {
      setProjectSummary(null);
      return;
    }
    fetchProject(projectId)
      .then((project) => {
        if (!cancelled) setProjectSummary(project);
      })
      .catch(() => {
        if (!cancelled) setProjectSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [persistedProject, projectId]);

  const customViews = useMemo(
    () => [
      ...(draftView ? [draftView] : []),
      ...serverViews.filter((view) => view.category !== "standard" && view.id !== draftView?.id),
      ...localViews.filter((view) => view.id !== draftView?.id),
    ],
    [draftView, localViews, serverViews],
  );
  const activeCustomView = customViews.find((view) => view.id === activeView) ?? null;
  const selectedRow = data.rows.find((row) => rowKey(row) === selectedRowId) ?? data.rows[0] ?? null;
  const selectionFamily = selectedRow?.family ? String(selectedRow.family) : null;
  const selectionPhase = selectedRow?.phase ? String(selectedRow.phase) : selectedRow?.conditionKey ? String(selectedRow.conditionKey) : null;
  const pivotLabel = activeView === "home" ? data.pivotViews[0]?.label || "alpha sweep" : null;

  useEffect(() => {
    setSelectedRowId((current) => current ?? (data.rows[0] ? rowKey(data.rows[0]) : null));
  }, [data.rows]);

  useEffect(() => {
    setDraftView(null);
    setLocalViews(isUuidLike(projectId) ? [] : readLocalV4Views(projectId, branch));
  }, [projectId, branch]);

  useEffect(() => {
    setActiveView(activeViewFromSearch(location.search));
  }, [branch, location.search, projectId]);

  useEffect(() => {
    let cancelled = false;
    if (!isUuidLike(projectId)) {
      setServerViews([]);
      return;
    }
    fetchThreadV4Views(projectId, branch)
      .then((response) => {
        if (!cancelled) setServerViews(response.views);
      })
      .catch((error) => {
        if (!cancelled) setStatusMessage(`Views offline: ${error instanceof Error ? error.message : String(error)}`);
      });
    return () => {
      cancelled = true;
    };
  }, [branch, projectId]);

  useEffect(() => {
    let cancelled = false;
    if (!isUuidLike(projectId)) {
      setNotebookEntries(readLocalV4Notebook(projectId, branch));
      return;
    }
    fetchThreadV4Notebook(projectId, branch)
      .then((response) => {
        if (!cancelled) setNotebookEntries(response.entries);
      })
      .catch((error) => {
        if (!cancelled) setStatusMessage(`Notebook offline: ${error instanceof Error ? error.message : String(error)}`);
      });
    return () => {
      cancelled = true;
    };
  }, [branch, projectId]);

  const addNotebookEntry = useCallback(() => {
    const body = notebookDraft.trim();
    if (!body) return;
    const sourceType = notebookSourceType === "manual" && notebookType === "agent" ? "agent" : notebookSourceType;
    const sourceUri = notebookSourceUri.trim();
    const optimistic = localNotebookEntry(projectId, branch, notebookType, sourceType, sourceUri, notebookVisibility, body);
    setNotebookDraft("");
    setNotebookSourceUri("");

    if (!isUuidLike(projectId)) {
      setNotebookEntries((current) => {
        const next = [optimistic, ...current];
        writeLocalV4Notebook(projectId, branch, next);
        return next;
      });
      return;
    }
    setNotebookEntries((current) => [optimistic, ...current]);
    createThreadV4NotebookEntry({
      project_id: projectId,
      branch,
      entry_type: notebookType,
      author_label: noteAuthorLabel(notebookType),
      visibility: notebookVisibility,
      source_type: sourceType,
      source_uri: sourceUri || null,
      body,
      anchors: { view: String(activeView), row_id: selectedRowId },
      source_metadata: { captured_by: "thread-v4-workbench" },
      metadata: { source: "thread-v4-workbench" },
    })
      .then((saved) => {
        setNotebookEntries((current) => current.map((entry) => entry.id === optimistic.id ? saved : entry));
      })
      .catch((error) => setStatusMessage(`Notebook saved locally: ${error instanceof Error ? error.message : String(error)}`));
  }, [activeView, branch, notebookDraft, notebookSourceType, notebookSourceUri, notebookType, notebookVisibility, projectId, selectedRowId]);

  const notebook = {
    entries: notebookEntries,
    draft: notebookDraft,
    type: notebookType,
    sourceType: notebookSourceType,
    sourceUri: notebookSourceUri,
    visibility: notebookVisibility,
    onDraftChange: setNotebookDraft,
    onTypeChange: setNotebookType,
    onSourceTypeChange: setNotebookSourceType,
    onSourceUriChange: setNotebookSourceUri,
    onVisibilityChange: setNotebookVisibility,
    onAdd: addNotebookEntry,
  };

  const goToView = useCallback((nextView: V4ActiveViewId, options?: { replace?: boolean }) => {
    setActiveView(nextView);
    const nextSearchParams = new URLSearchParams(location.search);
    if (nextView === "home") {
      nextSearchParams.delete("view");
    } else {
      nextSearchParams.set("view", String(nextView));
    }
    const nextSearch = nextSearchParams.toString();
    const currentSearch = location.search.startsWith("?") ? location.search.slice(1) : location.search;
    if (nextSearch === currentSearch) return;
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: options?.replace ?? false },
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("view")?.trim();
    if (requested === "cfd" || requested === "data" || requested === "analysis" || requested === "power") {
      goToView("home", { replace: true });
    }
  }, [goToView, location.search]);

  const createCustomView = useCallback(() => {
    const draft = makeDraftView(projectId, branch);
    setDraftView(draft);
    goToView(draft.id);
    setStatusMessage("Draft view ready");
  }, [branch, goToView, projectId]);

  const updateViewInMemory = useCallback((viewId: string, update: (view: ThreadV4ViewSpec) => ThreadV4ViewSpec, fallback?: ThreadV4ViewSpec) => {
    setDraftView((current) => current?.id === viewId ? update(current) : fallback?.id === viewId ? update(fallback) : current);
    setServerViews((current) => current.map((view) => view.id === viewId ? update(view) : view));
    setLocalViews((current) => {
      const next = current.map((view) => view.id === viewId ? update(view) : view);
      if (next !== current && next.some((view, index) => view !== current[index])) writeLocalV4Views(projectId, branch, next);
      return next;
    });
  }, [branch, projectId]);

  const ensureDraftForEditing = useCallback(() => {
    if (draftView) return draftView;
    if (activeCustomView) {
      setDraftView((current) => current ?? activeCustomView);
      return activeCustomView;
    }
    const draft = makeDraftView(projectId, branch);
    setDraftView(draft);
    return draft;
  }, [activeCustomView, branch, draftView, projectId]);

  const customizeActiveView = useCallback(() => {
    ensureDraftForEditing();
    goToView("customize");
    setStatusMessage("Customize mode");
  }, [ensureDraftForEditing, goToView]);

  const addWidgetToEditingView = useCallback((kind: ThreadV4WidgetKind) => {
    const editing = ensureDraftForEditing();
    updateViewInMemory(editing.id, (view) => appendWidgetToView(view, kind), editing);
    setStatusMessage(`${WIDGET_LABELS[kind]} added`);
  }, [ensureDraftForEditing, updateViewInMemory]);

  const autoArrangeEditingView = useCallback(() => {
    const editing = ensureDraftForEditing();
    updateViewInMemory(editing.id, (view) => ({ ...view, layout_json: autoArrangeThreadV4Widgets(view.layout_json) }), editing);
    setStatusMessage("View auto-arranged");
  }, [ensureDraftForEditing, updateViewInMemory]);

  const renameEditingView = useCallback((name: string) => {
    const editing = draftView ?? activeCustomView;
    if (!editing) return;
    updateViewInMemory(editing.id, (view) => ({ ...view, name }));
  }, [activeCustomView, draftView, updateViewInMemory]);

  const dockWidgetInActiveView = useCallback((widgetId: string, dock: ThreadV4WidgetDock) => {
    if (!activeCustomView) return;
    updateViewInMemory(activeCustomView.id, (view) => ({ ...view, layout_json: dockThreadV4Widget(view.layout_json, widgetId, dock) }));
  }, [activeCustomView, updateViewInMemory]);

  const dockWidgetInEditingView = useCallback((widgetId: string, dock: ThreadV4WidgetDock) => {
    const editing = draftView ?? activeCustomView;
    if (!editing) return;
    updateViewInMemory(editing.id, (view) => ({ ...view, layout_json: dockThreadV4Widget(view.layout_json, widgetId, dock) }));
  }, [activeCustomView, draftView, updateViewInMemory]);

  const toggleWidgetLockInActiveView = useCallback((widgetId: string) => {
    if (!activeCustomView) return;
    updateViewInMemory(activeCustomView.id, (view) => ({
      ...view,
      layout_json: {
        ...view.layout_json,
        widgets: view.layout_json.widgets.map((widget) => {
          if (widget.id !== widgetId) return widget;
          const locked = widget.bindings?.locked === true || widget.state?.locked === true;
          return { ...widget, bindings: { ...widget.bindings, locked: !locked } };
        }),
      },
    }));
  }, [activeCustomView, updateViewInMemory]);

  const toggleWidgetLockInEditingView = useCallback((widgetId: string) => {
    const editing = draftView ?? activeCustomView;
    if (!editing) return;
    updateViewInMemory(editing.id, (view) => ({
      ...view,
      layout_json: {
        ...view.layout_json,
        widgets: view.layout_json.widgets.map((widget) => {
          if (widget.id !== widgetId) return widget;
          const locked = widget.bindings?.locked === true || widget.state?.locked === true;
          return { ...widget, bindings: { ...widget.bindings, locked: !locked } };
        }),
      },
    }));
  }, [activeCustomView, draftView, updateViewInMemory]);

  const saveNamedView = useCallback((name: string, visibility: ThreadV4ViewVisibility, isDefault: boolean) => {
    const base = activeCustomView ?? draftView ?? makeCustomDashboardTemplate(projectId, branch, Date.now().toString(36));
    const draft = {
      ...base,
      name: name.trim() || "Tsangpo CFD review",
      visibility,
      is_default: isDefault,
      metadata: { ...base.metadata, draft: false, source: "thread-v4-workbench" },
    };
    setDialog(null);
    if (!isUuidLike(projectId)) {
      setLocalViews((current) => {
        const next = [...current.filter((view) => view.id !== draft.id), draft];
        writeLocalV4Views(projectId, branch, next);
        return next;
      });
      setDraftView((current) => current?.id === draft.id ? null : current);
      goToView(draft.id);
      setStatusMessage("View saved locally");
      return;
    }
    if (activeCustomView && isUuidLike(activeCustomView.id)) {
      updateThreadV4View(activeCustomView.id, {
        name: draft.name,
        visibility,
        layout_json: activeCustomView.layout_json,
        metadata: { ...activeCustomView.metadata, source: "thread-v4-workbench" },
        is_default: isDefault,
      })
        .then((saved) => {
          setServerViews((current) => current.map((view) => view.id === saved.id ? saved : view));
          goToView(saved.id);
          setStatusMessage("View saved");
        })
        .catch((error) => setStatusMessage(`Save failed: ${error instanceof Error ? error.message : String(error)}`));
      return;
    }
    createThreadV4View({
      project_id: projectId,
      branch,
      name: draft.name,
      slug: draft.slug,
      category: "custom",
      visibility,
      layout_json: draft.layout_json,
      metadata: draft.metadata,
      is_default: isDefault,
    })
      .then((saved) => {
        setServerViews((current) => [...current, saved]);
        setDraftView((current) => current?.id === draft.id ? null : current);
        goToView(saved.id);
        setStatusMessage("View saved");
      })
      .catch((error) => setStatusMessage(`Save failed: ${error instanceof Error ? error.message : String(error)}`));
  }, [activeCustomView, branch, draftView, goToView, projectId]);

  const stageProps = { data, selectedRowId, onSelectRow: setSelectedRowId, notebook, projectId, persistedProject, projectName: displayProjectName };
  const workbench = (
    <main className="v4 v4-app thread-v4-workbench" data-testid="thread-v4-workbench">
      <TopChrome
        projectName={displayProjectName}
        scopeLabel={scopeLabel}
        branch={branch}
        status={`${sourceStatus} · ${persistenceStatus}`}
        persistedProject={persistedProject}
        onAskAgent={customizeActiveView}
      />
      <ViewTabs active={activeView} customViews={customViews} onChange={goToView} onCustomize={customizeActiveView} onAddView={createCustomView} onSave={() => setDialog("save")} />
      <div className="v4-app-body">
        {activeView !== "customize" ? (
          <SelectionRail
            branch={branch}
            rows={data.totalRowCount || data.rows.length}
            cols={data.columns.length}
            assets={data.assets.length}
            nodes={data.provenance.representedScale.totalNodes}
            edges={data.provenance.representedScale.totalEdges}
            selectedRow={selectedRowId}
            projectName={displayProjectName}
            family={selectionFamily}
            phase={selectionPhase}
            pivot={pivotLabel}
          />
        ) : <span className="v4-rail-placeholder" />}
        <section className="v4-app-stage">
          {activeView === "home" ? <DefaultThreadView {...stageProps} /> : null}
          {activeView === "customize" ? (
            <CustomizeFlow
              {...stageProps}
              draftView={draftView ?? activeCustomView}
              onAddWidget={addWidgetToEditingView}
              onAutoArrange={autoArrangeEditingView}
              onDockWidget={dockWidgetInEditingView}
              onToggleWidgetLock={toggleWidgetLockInEditingView}
              onNameChange={renameEditingView}
              onSave={() => setDialog("save")}
              onClose={() => goToView((draftView ?? activeCustomView)?.id ?? "home")}
            />
          ) : null}
          {activeCustomView ? <CustomLayoutView view={activeCustomView} {...stageProps} onDockWidget={dockWidgetInActiveView} onToggleWidgetLock={toggleWidgetLockInActiveView} /> : null}
          {dialog === "save" ? <SaveDialog defaultName={activeCustomView?.name || draftView?.name || "Tsangpo CFD review"} onClose={() => setDialog(null)} onSave={saveNamedView} /> : null}
        </section>
      </div>
    </main>
  );
  return (
    <ProvenanceProvider projectId={projectId}>
      <ViewStateProvider>
        <PlotDataProvider>{workbench}</PlotDataProvider>
      </ViewStateProvider>
    </ProvenanceProvider>
  );
}
